export type ColorSchemeMode = 'standard' | 'high_precision' | 'threshold_focus';
export type GridScalingMode = 'linear' | 'quadratic' | 'logarithmic' | 'sigmoid';

export type GridSettings = {
  threshold: number;
  opacity: number;
  modulated: boolean;
  blur: number;
  radius: number;
  colorScheme: ColorSchemeMode;
  scalingMode: GridScalingMode;
  focusRange: [number, number];
  customFocusRange: boolean;
  intensityGamma: number;
  customGamma: boolean;
};

export type GridSettingValue = Partial<GridSettings> | undefined;

const COLOR_SCHEMES: ColorSchemeMode[] = ['standard', 'high_precision', 'threshold_focus'];
const SCALING_MODES: GridScalingMode[] = ['linear', 'quadratic', 'logarithmic', 'sigmoid'];

export const DEFAULT_ANALYSIS_GRID_SETTINGS: GridSettings = {
  threshold: 0.05,
  opacity: 0.85,
  modulated: false,
  blur: 20,
  radius: 12,
  colorScheme: 'high_precision',
  scalingMode: 'linear',
  focusRange: [0.65, 0.9],
  customFocusRange: false,
  intensityGamma: 0.55,
  customGamma: false,
};

const LEGACY_HEATMAP_THRESHOLD = 0.55;

export const sanitizeGridSettings = (value: GridSettingValue): GridSettings => {
  const base: GridSettings = { ...DEFAULT_ANALYSIS_GRID_SETTINGS, ...(value ?? {}) };
  let threshold = typeof base.threshold === 'number' ? base.threshold : DEFAULT_ANALYSIS_GRID_SETTINGS.threshold;

  if (Math.abs(threshold - LEGACY_HEATMAP_THRESHOLD) < 1e-6) {
    threshold = DEFAULT_ANALYSIS_GRID_SETTINGS.threshold;
  }

  threshold = Math.max(DEFAULT_ANALYSIS_GRID_SETTINGS.threshold, Math.min(0.9, threshold));
  const opacity = typeof base.opacity === 'number' ? Math.max(0, Math.min(1, base.opacity)) : DEFAULT_ANALYSIS_GRID_SETTINGS.opacity;
  const modulated = typeof base.modulated === 'boolean' ? base.modulated : DEFAULT_ANALYSIS_GRID_SETTINGS.modulated;

  const blur =
    typeof base.blur === 'number'
      ? Math.max(5, Math.min(80, Math.round(base.blur)))
      : DEFAULT_ANALYSIS_GRID_SETTINGS.blur;

  const radius =
    typeof base.radius === 'number'
      ? Math.max(2, Math.min(20, Math.round(base.radius)))
      : DEFAULT_ANALYSIS_GRID_SETTINGS.radius;

  const colorScheme: ColorSchemeMode = COLOR_SCHEMES.includes(base.colorScheme)
    ? base.colorScheme
    : DEFAULT_ANALYSIS_GRID_SETTINGS.colorScheme;

  const scalingMode: GridScalingMode = SCALING_MODES.includes(base.scalingMode)
    ? base.scalingMode
    : DEFAULT_ANALYSIS_GRID_SETTINGS.scalingMode;

  let focusRange: [number, number] = DEFAULT_ANALYSIS_GRID_SETTINGS.focusRange;
  if (Array.isArray(base.focusRange) && base.focusRange.length >= 2) {
    const [rawMin, rawMax] = base.focusRange;
    const minVal = Number.isFinite(rawMin) ? Number(rawMin) : DEFAULT_ANALYSIS_GRID_SETTINGS.focusRange[0];
    const maxVal = Number.isFinite(rawMax) ? Number(rawMax) : DEFAULT_ANALYSIS_GRID_SETTINGS.focusRange[1];
    const clampedMin = Math.max(0, Math.min(0.98, Math.min(minVal, maxVal - 0.01)));
    const clampedMax = Math.min(1, Math.max(clampedMin + 0.01, maxVal));
    focusRange = [Number(clampedMin.toFixed(3)), Number(clampedMax.toFixed(3))];
  }

  const customFocusRange =
    typeof base.customFocusRange === 'boolean' ? base.customFocusRange : DEFAULT_ANALYSIS_GRID_SETTINGS.customFocusRange;

  const intensityGamma =
    typeof base.intensityGamma === 'number'
      ? Number(Math.max(0.1, Math.min(6, base.intensityGamma)).toFixed(2))
      : DEFAULT_ANALYSIS_GRID_SETTINGS.intensityGamma;

  const customGamma =
    typeof base.customGamma === 'boolean' ? base.customGamma : DEFAULT_ANALYSIS_GRID_SETTINGS.customGamma;

  return {
    threshold,
    opacity,
    modulated,
    blur,
    radius,
    colorScheme,
    scalingMode,
    focusRange,
    customFocusRange,
    intensityGamma,
    customGamma,
  };
};

const applySanitizedEntry = (
  target: globalThis.Map<string, GridSettings>,
  key: unknown,
  value: GridSettingValue
) => {
  if (typeof key !== 'string' || !key) {
    return;
  }
  try {
    target.set(key, sanitizeGridSettings(value));
  } catch {
    // Ignore malformed entries and continue processing
  }
};

export const deserializeGridSettings = (input: unknown): globalThis.Map<string, GridSettings> => {
  const map = new globalThis.Map<string, GridSettings>();

  if (!input) {
    return map;
  }

  if (Array.isArray(input)) {
    input.forEach((entry) => {
      if (Array.isArray(entry) && entry.length >= 2) {
        applySanitizedEntry(map, entry[0], entry[1] as GridSettingValue);
        return;
      }

      if (entry && typeof entry === 'object' && 'key' in entry && 'value' in entry) {
        const { key, value } = entry as { key: unknown; value: GridSettingValue };
        applySanitizedEntry(map, key, value);
      }
    });
    return map;
  }

  if (typeof input === 'object') {
    Object.entries(input as Record<string, GridSettingValue>).forEach(([key, value]) => {
      applySanitizedEntry(map, key, value);
    });
  }

  return map;
};

export const serializeGridSettings = (
  map: globalThis.Map<string, GridSettings>
): Record<string, GridSettings> => {
  const result: Record<string, GridSettings> = {};
  map.forEach((value, key) => {
    result[key] = sanitizeGridSettings(value);
  });
  return result;
};
